import { div, isMobile, nop, popupPrefix, resize } from './common';

// eslint-disable-next-line import/no-unresolved
import closeIcon from '!!raw-loader!@webapp/ui/lib/icons/bold-cross.svg?raw';

let resizeTimeout = null;

export const resizePopupContent = (
    iframe: HTMLIFrameElement,
    popup: Popup,
    maxHeight?: number,
    cb?: (h: number) => void
): void => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
        resize(
            iframe,
            ({ height }) => {
                const h = Math.ceil(height as number);
                const popupRect = popup.getContentDim();
                if (!popupRect) {
                    return;
                }
                if (h > popupRect.height + 5) {
                    popup.enableScroll();
                } else {
                    popup.disableScroll();
                }
                cb && cb(h);
            },
            maxHeight
        );
    }, 100);
};

export class Popup {
    private readonly popup: HTMLDivElement;
    private readonly bg: HTMLDivElement;
    private readonly contentContainer: HTMLDivElement;
    private readonly rootContainer: HTMLDivElement;
    private readonly closeButton: HTMLDivElement;
    private openCb = nop;
    private closeCb = nop;
    private closeTimer = null;

    // eslint-disable-next-line @typescript-eslint/member-ordering
    constructor({
        className,
        close,
        content,
        elementColor,
        id,
        width,
        height,
        position,
        borderRadius,
        hideContainer
    }: {
        className?: string;
        content: HTMLElement;
        elementColor: string;
        close: boolean;
        id: string;
        width?: string;
        height?: string;
        position?: string;
        borderRadius?: string;
        hideContainer?: boolean;
    }) {
        this.popup = div();
        this.bg = div();
        this.contentContainer = div();
        this.rootContainer = div();

        this.bg.classList.add(`${popupPrefix}bg`);
        this.bg.setAttribute('id', `${popupPrefix}bg`);
        this.contentContainer.classList.add(`${popupPrefix}content`);
        this.rootContainer.classList.add(`${popupPrefix}container`);

        if (isMobile) {
            this.rootContainer.classList.add('ttgraf-mobile');
        }

        if (hideContainer) {
            this.rootContainer.classList.add(`${popupPrefix}container_hide`);
        }

        if (close) {
            this.closeButton = div();
            this.closeButton.innerHTML = closeIcon;
            this.closeButton.classList.add(`${popupPrefix}close`);
            this.closeButton.addEventListener('click', this.close);
            this.bg.addEventListener('click', this.close);
            this.rootContainer.appendChild(this.closeButton);
        }

        this.popup.setAttribute('id', id);
        this.popup.classList.add('ttgraf-popup');
        className && this.popup.classList.add(className);

        this.rootContainer.appendChild(this.contentContainer);
        this.contentContainer.appendChild(content);
        this.popup.appendChild(this.rootContainer);

        this.popup.style.setProperty(`--tt-elementColor`, elementColor || '#3190ff');
        this.popup.style.width = width;
        this.popup.style.height = height;
        this.contentContainer.style.borderRadius = borderRadius;

        if (position) {
            this.popup.classList.add(`ttgraf-popup_${position.toLowerCase()}`);
        }

        document.body.appendChild(this.popup);
    }

    public getContentDim = (): DOMRect => this.contentContainer.getBoundingClientRect();

    public enableScroll = (): void => {
        this.contentContainer.style.overflowY = 'auto';
    };

    public disableScroll = (): void => {
        this.contentContainer.style.overflowY = 'hidden';
    };

    public scrollTop = (): void => {
        this.contentContainer.scrollTop = 0;
    };

    public open = (): void => {
        clearTimeout(this.closeTimer);
        this.popup.classList.remove(`${popupPrefix}closed`);
        this.popup.classList.add(`${popupPrefix}opened`);
        document.body.appendChild(this.bg);
        this.openCb();
    };

    public getContentHeight = (): number =>
        this.contentContainer ? Math.ceil(this.contentContainer.getBoundingClientRect().height) : 0;

    public isOpened = (): boolean => this.popup.classList.contains(`${popupPrefix}opened`);

    public close = (): void => {
        clearTimeout(this.closeTimer);
        this.popup.classList.remove(`${popupPrefix}opened`);
        this.popup.classList.add(`${popupPrefix}closed`);
        document.getElementById(`${popupPrefix}bg`).remove();
        this.closeCb();
        this.closeTimer = setTimeout(() => {
            this.popup.classList.remove(`${popupPrefix}closed`);
        }, 300);
    };

    public onOpen = (fn: () => void): void => {
        this.openCb = fn;
    };

    public onClose = (fn: () => void): void => {
        this.closeCb = fn;
    };

    public setHeight = (height: string): void => {
        this.popup.style.height = height;
    };

    public showContainer = () => {
        this.rootContainer.classList.remove(`${popupPrefix}container_hide`);
    };
}

export const createShowNeverButton = (text: string, bgColor: string, theme: string): HTMLButtonElement => {
    const btn = document.createElement('button');
    btn.classList.add(`${popupPrefix}reopen__button`);
    btn.innerText = text;
    btn.style.background = bgColor;

    if (theme === 'dark') {
        btn.style.color = '#000';
    }

    return btn;
};
